import { useCallback, useState } from "react"

import { TokenMutationResult } from "services/token/type"

export type TokenGateState = "ok" | "disabled" | "unavailable"

/**
 * Classify a failed $TOKEN call into the state the tab should render.
 *
 * The three cases mean different things to an operator and must not be
 * collapsed:
 *
 *   disabled    — 403 TOKEN_DISABLED. The gate (`tokenEnabled &&
 *                 isMicroserviceOwner`) is closed for this tenant. Configuration,
 *                 not an outage; retrying is pointless and offering a Retry
 *                 button would be a lie.
 *   unavailable — 503. token-service is unreachable. Retry is the right advice.
 *   ok          — anything else, including a genuinely empty tenant. A tenant
 *                 with no purchases yet is the NORMAL pre-launch state and must
 *                 render as an empty table, never as an error.
 */
export const classifyTokenFailure = (res: TokenMutationResult<unknown>): TokenGateState => {
  if (res.code === "TOKEN_DISABLED") return "disabled"
  if (res.status === 503) return "unavailable"
  return "ok"
}

/**
 * Gate state plus a loader that keeps it up to date.
 *
 * `run` returns the payload on success and `undefined` on failure, so a caller
 * can keep the happy path linear while the gate state updates behind it.
 */
export const useTokenGate = () => {
  const [gate, setGate] = useState<TokenGateState>("ok")
  const [loading, setLoading] = useState(false)

  const run = useCallback(async <T,>(call: () => Promise<TokenMutationResult<T>>): Promise<T | undefined> => {
    setLoading(true)
    try {
      const res = await call()
      if (res.ok) {
        // Reset on success: a tenant switch from a disabled tenant to an enabled
        // one must clear the previous tenant's gate state, or the panel stays
        // stuck showing "not enabled" for a tenant that is.
        setGate("ok")
        return res.data
      }
      setGate(classifyTokenFailure(res))
      return undefined
    } finally {
      setLoading(false)
    }
  }, [])

  return { gate, loading, run, setGate }
}
